import { FilePdfOutlined, LinkOutlined, MailOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Dropdown,
  Flex,
  type MenuProps,
  Space,
  Tag,
  Typography
} from "antd"
import React, { useMemo } from "react"
import { Link } from "react-router-dom"

import Icon from "../../../../Icon"
import { empty } from "../../../../constants/placeholders"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { cn } from "../../../../utils/cn.ts"
import { getDeclineReasonForDisplay } from "../../../../utils/documents/document-decline-outcome.ts"
import { formatNumber } from "../../../../utils/number/formate.ts"
import {
  PROPOSAL_SHARE_EMAIL_STATUSES,
  PROPOSAL_STATUSES_OBJ
} from "../../constants/proposal-statuses-keys.ts"
import { emailProposalToClient } from "../../redux/proposals.thunks.ts"
import { type ProposalListItem } from "../../types/types.ts"

const { Title, Text } = Typography

interface ProposalsSidebarListItemProps {
  isActive?: boolean
  onSelect: (id: string) => void
  viewLeadAllowed?: boolean
  proposal: ProposalListItem
}

export function ProposalsSidebarListItem({
  isActive,
  viewLeadAllowed,
  proposal,
  onSelect
}: ProposalsSidebarListItemProps) {
  const dispatch = useAppDispatch()
  const date = proposal?.reviewed_at || proposal?.submitted_for_review_at

  const submittedAttachment = proposal?.submitted_attachment
  const version = submittedAttachment?.full_version || proposal?.version_display
  const statusData = PROPOSAL_STATUSES_OBJ?.[proposal?.status]
  const displayReason = getDeclineReasonForDisplay(proposal?.decline_reason)

  const onClickHandler = () => onSelect(proposal?.id)

  const kebabMenuItems: MenuProps["items"] = useMemo(() => {
    const list = []

    if (PROPOSAL_SHARE_EMAIL_STATUSES.includes(proposal?.status)) {
      list.push({
        key: "1",
        label: "Share via Email",
        onClick: () => dispatch(emailProposalToClient(proposal.id)),
        icon: <MailOutlined />
      })
    }

    if (viewLeadAllowed && proposal?.lead_id) {
      list.push({
        key: "2",
        label: (
          <Link target={"_blank"} to={RoutesPaths.lead.view(proposal.lead_id)}>
            Open Lead
          </Link>
        ),
        icon: <LinkOutlined />
      })
    }

    return list
  }, [
    dispatch,
    viewLeadAllowed,
    proposal.id,
    proposal.lead_id,
    proposal?.proposal_id,
    proposal?.status
  ])

  return (
    <Card
      size={"small"}
      onClick={onClickHandler}
      className={cn(
        "mx-0.5 mb-3 transition-colors duration-300 hover:bg-primary/5 cursor-pointer border-primary/50",
        isActive && "border-primary outline outline-primary bg-primary/5"
      )}
    >
      <Flex justify={"space-between"} className={"pb-3"}>
        <div>
          <Title
            level={5}
            ellipsis={{ rows: 2, tooltip: proposal?.proposal_id }}
          >
            {proposal?.proposal_id}
          </Title>
          <Flex vertical>
            <Text type={"secondary"}>{proposal?.customer_name}</Text>
            <Text type={"secondary"}>{proposal?.lead_name}</Text>
          </Flex>
        </div>
        <Flex vertical align={"end"} gap={"small"}>
          <Tag style={statusData?.style} className={"m-0"}>
            {statusData?.label}
          </Tag>
          {kebabMenuItems.length > 0 ? (
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown
                menu={{ items: kebabMenuItems }}
                trigger={["click"]}
                placement="bottom"
              >
                <Button type={"text"} icon={<Icon type={"VerticalDots"} />} />
              </Dropdown>
            </div>
          ) : null}
        </Flex>
      </Flex>
      {displayReason ? (
        <Text type="secondary" className="text-xs line-clamp-2 mb-2 block">
          {displayReason}
        </Text>
      ) : null}
      <Flex justify={"space-between"}>
        <Space>
          <Text className={"text-xs inline-flex items-center gap-1"}>
            <Icon type={"Calendar"} />
            {date || empty}
          </Text>
          {version && (
            <Text className={"text-xs inline-flex items-center gap-1"}>
              <FilePdfOutlined />
              {version}
            </Text>
          )}
        </Space>
        <Text>{formatNumber(proposal?.total_amount)}</Text>
      </Flex>
    </Card>
  )
}
